import { useState } from 'react';

type RenovationType = 'kitchen' | 'bathroom' | 'basement' | 'addition' | 'flooring' | 'exterior';

const types: { id: RenovationType; label: string; icon: string }[] = [
  { id: 'kitchen', label: 'Kitchen', icon: 'ri-goblet-line' },
  { id: 'bathroom', label: 'Bathroom', icon: 'ri-drop-line' },
  { id: 'basement', label: 'Basement', icon: 'ri-building-4-line' },
  { id: 'addition', label: 'Additions', icon: 'ri-add-box-line' },
  { id: 'flooring', label: 'Flooring', icon: 'ri-layout-grid-line' },
  { id: 'exterior', label: 'Exterior', icon: 'ri-home-5-line' },
];

type RenovationData = {
  tagline: string;
  title: string;
  description: string;
  priceRange: string;
  timeline: string;
  image: string;
  highlights: { icon: string; label: string; note: string }[];
};

const renovationData: Record<RenovationType, RenovationData> = {
  kitchen: {
    tagline: 'The Heart of the Home',
    title: 'Kitchen Renovations',
    description:
      'From a simple refresh to a full gut-and-rebuild, we transform kitchens into functional, beautiful spaces. New cabinets, countertops, islands, lighting, and everything in between — designed around how you actually cook and live.',
    priceRange: '$18,000 – $65,000+',
    timeline: '3 – 8 weeks',
    image:
      'https://public.readdy.ai/ai/img_res/44fb0b56bbf53b21f527eb5776a7e7de.jpg',
    highlights: [
      { icon: 'ri-tools-line', label: 'Custom Cabinetry', note: 'Built-to-spec or pre-fab options' },
      { icon: 'ri-layout-2-line', label: 'Counter & Island Install', note: 'Quartz, granite, or butcher block' },
      { icon: 'ri-lightbulb-line', label: 'Lighting Design', note: 'Pot lights, pendants & under-cabinet' },
      { icon: 'ri-water-flash-line', label: 'Plumbing Upgrades', note: 'Sink, faucet & dishwasher hookups' },
      { icon: 'ri-plug-line', label: 'Electrical Work', note: 'New circuits, outlets & range hood' },
      { icon: 'ri-paint-brush-line', label: 'Full Finishing', note: 'Tile backsplash, trim & paint' },
    ],
  },
  bathroom: {
    tagline: 'Your Personal Retreat',
    title: 'Bathroom Renovations',
    description:
      'We turn outdated bathrooms into spa-like spaces. Walk-in showers, soaker tubs, double vanities, heated floors — whatever your vision, we make it happen cleanly, on schedule, and with zero surprises.',
    priceRange: '$8,000 – $35,000+',
    timeline: '1 – 4 weeks',
    image:
      'https://readdy.ai/api/search-image?query=Luxurious%20renovated%20bathroom%20with%20large%20walk-in%20tile%20shower%2C%20freestanding%20soaker%20tub%2C%20double%20floating%20vanity%20with%20vessel%20sinks%2C%20modern%20matte%20black%20fixtures%2C%20large%20format%20floor%20tile%2C%20natural%20stone%20accents%2C%20bright%20well-lit%20residential%20bathroom%2C%20Burlington%20Ontario%20home%20renovation&width=800&height=560&seq=reno-bath&orientation=landscape',
    highlights: [
      { icon: 'ri-drop-line', label: 'Shower & Tub Install', note: 'Walk-in showers, tubs & combo units' },
      { icon: 'ri-layout-2-line', label: 'Tile Work', note: 'Floor, wall & shower surround tile' },
      { icon: 'ri-home-gear-line', label: 'Vanity & Fixtures', note: 'Single or double vanity, mirrors & hardware' },
      { icon: 'ri-temp-hot-line', label: 'Heated Floors', note: 'In-floor electric heating systems' },
      { icon: 'ri-water-flash-line', label: 'Plumbing', note: 'All rough-in and fixture connections' },
      { icon: 'ri-eye-line', label: 'Ventilation', note: 'Proper exhaust fans & moisture control' },
    ],
  },
  basement: {
    tagline: 'Unlock Your Lower Level',
    title: 'Basement Finishing',
    description:
      'A finished basement can add hundreds of square feet of living space to your home. We handle everything from framing and insulation to drywall, flooring, and electrical — turning raw concrete into a space your family will love.',
    priceRange: '$25,000 – $70,000+',
    timeline: '6 – 14 weeks',
    image:
      'https://readdy.ai/api/search-image?query=Beautifully%20finished%20basement%20with%20pot%20lights%2C%20luxury%20vinyl%20plank%20flooring%2C%20built-in%20entertainment%20wall%2C%20comfortable%20sectional%20sofa%2C%20wet%20bar%20area%2C%20egress%20window%20with%20natural%20light%2C%20modern%20residential%20basement%20renovation%2C%20Burlington%20Ontario%20Canada%20home&width=800&height=560&seq=reno-basement&orientation=landscape',
    highlights: [
      { icon: 'ri-wall-line', label: 'Framing & Insulation', note: 'Proper thermal and moisture protection' },
      { icon: 'ri-plug-line', label: 'Electrical', note: 'Panel upgrades, pot lights & outlets' },
      { icon: 'ri-drop-line', label: 'Bathroom Rough-In', note: 'Full basement bath or 2-piece option' },
      { icon: 'ri-layout-grid-line', label: 'Flooring', note: 'LVP, carpet or tile throughout' },
      { icon: 'ri-tv-line', label: 'Rec Room & Media', note: 'Entertainment walls & feature areas' },
      { icon: 'ri-home-office-line', label: 'Home Office / Suite', note: 'Private bedroom or in-law suite' },
    ],
  },
  addition: {
    tagline: 'Grow Without Moving',
    title: 'Home Additions',
    description:
      'Need more space but love your neighbourhood? We build seamless additions that feel like they were always part of your home. Main floor extensions, second-storey additions, sunrooms — fully permitted and built to last.',
    priceRange: '$80,000 – $250,000+',
    timeline: '12 – 24 weeks',
    image:
      'https://readdy.ai/api/search-image?query=Modern%20home%20addition%20seamlessly%20connected%20to%20existing%20house%20exterior%2C%20new%20sunroom%20or%20family%20room%20addition%20with%20large%20windows%2C%20clean%20architectural%20connection%2C%20lush%20garden%20and%20backyard%20visible%2C%20professional%20residential%20construction%20photography%2C%20Burlington%20Ontario%20Canada%2C%20upscale%20home%20improvement&width=800&height=560&seq=reno-addition&orientation=landscape',
    highlights: [
      { icon: 'ri-file-text-line', label: 'Permits & Engineering', note: 'Fully permitted structural work' },
      { icon: 'ri-foundation-line', label: 'Foundation & Structure', note: 'Proper footings and framing' },
      { icon: 'ri-home-5-line', label: 'Roofing & Exterior', note: 'Matched to existing home finishes' },
      { icon: 'ri-plug-line', label: 'Electrical & HVAC', note: 'Extended from existing systems' },
      { icon: 'ri-sun-line', label: 'Sunrooms & Solariums', note: 'Year-round light-filled spaces' },
      { icon: 'ri-arrow-up-box-line', label: 'Second Storey Additions', note: 'Full upper floor builds' },
    ],
  },
  flooring: {
    tagline: 'The Foundation of Every Room',
    title: 'Flooring Installation',
    description:
      'New floors instantly transform how a space looks and feels. We install hardwood, engineered wood, luxury vinyl plank, tile, and carpet throughout Burlington-area homes — supply & install or install-only.',
    priceRange: '$4,000 – $25,000+',
    timeline: '1 – 2 weeks',
    image:
      'https://readdy.ai/api/search-image?query=Beautiful%20new%20hardwood%20flooring%20installation%20in%20open%20concept%20living%20room%20and%20dining%20area%2C%20light%20oak%20wide%20plank%20floors%2C%20modern%20furniture%2C%20bright%20natural%20daylight%2C%20residential%20interior%20renovation%20photography%2C%20Burlington%20Ontario%20home%20improvement%2C%20professional%20installation%20result&width=800&height=560&seq=reno-flooring&orientation=landscape',
    highlights: [
      { icon: 'ri-layout-grid-line', label: 'Hardwood & Engineered', note: 'Solid or floating installation' },
      { icon: 'ri-grid-line', label: 'Luxury Vinyl Plank', note: 'Waterproof & durable for any room' },
      { icon: 'ri-artboard-line', label: 'Ceramic & Porcelain Tile', note: 'Kitchens, bathrooms & entryways' },
      { icon: 'ri-border-all-line', label: 'Subfloor Prep', note: 'Levelling, underlayment & vapour barrier' },
      { icon: 'ri-scissors-cut-line', label: 'Stairs & Transitions', note: 'Matching stair nose and trim work' },
      { icon: 'ri-shield-check-line', label: 'Removal & Disposal', note: 'Old floor tear-out included' },
    ],
  },
  exterior: {
    tagline: 'Curb Appeal & Protection',
    title: 'Exterior Renovations',
    description:
      'Your home\'s exterior is the first impression it makes. We handle siding replacement, soffit and fascia, window and door replacements, and exterior painting — protecting your home and boosting its value.',
    priceRange: '$6,000 – $60,000+',
    timeline: '1 – 6 weeks',
    image:
      'https://readdy.ai/api/search-image?query=Beautifully%20renovated%20home%20exterior%20with%20new%20fiber%20cement%20siding%2C%20freshly%20painted%20trim%2C%20new%20front%20door%20and%20windows%2C%20updated%20soffit%20and%20fascia%2C%20manicured%20front%20garden%2C%20charming%20suburban%20home%20curb%20appeal%2C%20Burlington%20Ontario%20Canada%20residential%20renovation%20photography&width=800&height=560&seq=reno-exterior&orientation=landscape',
    highlights: [
      { icon: 'ri-home-5-line', label: 'Siding Replacement', note: 'Vinyl, fibre cement or wood siding' },
      { icon: 'ri-door-open-line', label: 'Windows & Doors', note: 'Supply & install, all sizes' },
      { icon: 'ri-paint-brush-line', label: 'Exterior Painting', note: 'Full prep, prime & top coat' },
      { icon: 'ri-cloudy-2-line', label: 'Soffit & Fascia', note: 'Replacement and repair' },
      { icon: 'ri-water-percent-line', label: 'Caulking & Weatherproofing', note: 'Air sealing and moisture barriers' },
      { icon: 'ri-hammer-line', label: 'Trim & Moulding', note: 'Corners, columns and architectural detail' },
    ],
  },
};

export default function RenovationTypes() {
  const [active, setActive] = useState<RenovationType>('kitchen');
  const data = renovationData[active];

  return (
    <section className="bg-[#F7F5F1] py-20 px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <p className="text-[#C8A97E] text-xs font-bold tracking-[0.4em] uppercase mb-4">WHAT WE RENOVATE</p>
          <h2
            className="text-[#2B5741] text-4xl font-bold mb-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Our Renovation Services
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            Select a renovation type to see what we can do for your home.
          </p>
        </div>

        {/* Type selector */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {types.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                active === t.id
                  ? 'bg-[#2B5741] text-white'
                  : 'bg-white border border-gray-200 text-gray-500 hover:border-[#2B5741]/40 hover:text-[#2B5741]'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${t.icon} text-sm`}></i>
              </div>
              {t.label}
            </button>
          ))}
        </div>

        {/* Content panel */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Image */}
            <div className="lg:w-[420px] h-64 lg:h-auto flex-shrink-0">
              <img
                key={active}
                src={data.image}
                alt={data.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Info */}
            <div className="flex-1 p-7 flex flex-col justify-between">
              <div>
                <p className="text-[#C8A97E] text-[10px] font-bold uppercase tracking-[0.35em] mb-1">
                  {data.tagline}
                </p>
                <h3
                  className="text-[#2B5741] text-2xl font-bold mb-3"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  {data.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{data.description}</p>

                <div className="flex gap-6 mb-6">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Typical Range</p>
                    <p className="text-[#2B5741] font-bold text-sm">{data.priceRange}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-0.5">Timeline</p>
                    <p className="text-[#2B5741] font-bold text-sm">{data.timeline}</p>
                  </div>
                </div>
              </div>

              {/* Highlights grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {data.highlights.map((h) => (
                  <div key={h.label} className="flex items-start gap-2.5 bg-[#F7F5F1] rounded-lg p-3">
                    <div className="w-7 h-7 flex items-center justify-center bg-[#2B5741] rounded-md flex-shrink-0">
                      <i className={`${h.icon} text-white text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-gray-800 text-xs font-semibold">{h.label}</p>
                      <p className="text-gray-400 text-[11px] mt-0.5">{h.note}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}